// The map's root element is constantly getting modifier HTML classes set on it based on its current zoom and congestion (as described below).
// These classes allow the enclosed markers to be displayed according to CSS rules.
// See `app/stylesheets/online/apps/shared/_experience_marker.scss`.
//
// Map Zoom Modifiers
// Whenever the map zoom changes we set an HTML class modifier on the map's element eg. `zoom_3x` (actually `online_app__main__map--zoom_3x`).
// You can see the mapping between the Google map's zoom level and our labeling in zoom_label_calculator.js.
// These classes allow the enclosed markers to be displayed according to CSS rules.
//
// Map Marker Congestion Modifiers
// Whenever the map markers are refreshed (changing filters for example) we look at the number of markers and
// set an HTML class modifier on the map's element eg. `congestion_max (actually `online_app__main__map--congestion_max`) based on the congestion level.
// The congestion level is based on the number of markers for the entire map (note that this is *all* the markers, not just the ones that are clustered).
// You can see the threshold levels in marker_congestion_calculator.js.
//
// Clustering
// There is one clusterer used for the map.
// Much of the work around the clusterer can be seen in the `OnlineAppMap.replaceAllExperienceMarkers` method.
// This method is called when filters are changed.
// Importantly, the clusterer *only* clusters experience markers which are not monetizable with benefits ie. basically, experiences that are not on a paid plan.
// The experience markers that *are* monetizable with benefits (basically experiences on a paid plan) are never clustered and are added directly to the map.
// This means that experiences on a paid plan always appear as markers (not clustered) while other experiences "disappear" into clusters.
// The `ClustererCongestionCalculator` looks at the number of markers that are in the clusterer.
// It is then used to dynamically adjust the clusterer's maxZoom and minimumClusterSize based on congestion within the clusterer.
// Note that this is separate from the previously mentioned marker congestion that is for all the map's markers.
//
// Clusterer maxZoom
// The clusterer's maxZoom is dymamically adjusted based on the marker congestion level (see clusterer_congestion_calculator.js).
// The higher the congestion, the higher the maxZoom - meaning that with more markers the clustering continues as you zoom in more.
// And with less markers, the clustering will stop earlier as you zoom in.
//
// Clusterer minimumClusterSize
// The clusterer's minimumClusterSize is dymamically adjusted based on the marker congestion level (see clusterer_congestion_calculator.js).
// The higher the congestion, the higher the minimumClusterSize - meaning that with more markers the clustering happens with bigger groups of markers.
// And with less markers, the clustering will happen more readily with smaller groups of markers.
//

// import {MarkerClusterer} from "@googlemaps/markerclusterer"; // TODO - This this clustering library as it is recommended over `@google/markerclustererplus`. However there are significant API changes to be handled.
import MarkerClusterer from '@googlemaps/markerclustererplus';
import OnlineAppComponent from "../lib/online_app_component";
import ClustererCongestionCalculator from "../helpers/clusterer_congestion_calculator";
import MarkerCongestionCalculator from "../helpers/marker_congestion_calculator";
import getZoomLabel from "../helpers/zoom_label_calculator";

export const OnlineAppMapConfig = {
    baseHtmlClass: 'online_app__main__map',
    selector: '.online_app__main__map'
}

export class OnlineAppMap extends OnlineAppComponent {

    static getGoogleMarkers(experienceMarkers) {
        return experienceMarkers.map(function (experienceMarker) {
            experienceMarker.googleMarker.experienceMarker = experienceMarker;
            return experienceMarker.googleMarker;
        });
    }

    constructor(id_or_element, onlineApp) {
        super(id_or_element, onlineApp, OnlineAppMapConfig);
        if(!this.element) { return };

        this.initialZoom = 6;
        this.experienceMarkers = [];

        this.googleMapsApi = google.maps; // Always access the Google Maps API via this reference! Can be replaced with our own wrapper in the future.
        this.googleMap = new google.maps.Map(this.element, this.getGoogleMapOptions());

        this.setCurrentZoom();
        this.setCurrentMarkerCongestion();

        this.markerClusterer = new MarkerClusterer(
            this.googleMap,
            OnlineAppMap.getGoogleMarkers(this.experienceMarkers),
            this.getMarkerClusterOptions()
        );

        // When a cluster is clicked we've modified the standard zooming behaviour.
        // We extend the cluster's normal bounds by 10% so that the zoom-in when clicking a cluster is less.
        this.googleMapsApi.event.addListener(this.markerClusterer, 'clusterclick', (cluster) => {
            this.dispatchAction('clusterSelected', cluster);
            let clusterBounds = cluster.getBounds();

            let debug = false;

            // paint the original cluster bounds for debugging purposes
            if (debug) {
                let originalBounds = new this.googleMapsApi.LatLngBounds(clusterBounds.getSouthWest(), clusterBounds.getNorthEast());
                let originalRect = new this.googleMapsApi.Rectangle({
                    bounds: originalBounds,
                    strokeColor: '#908c8c',
                    fillOpacity: 0
                });
                originalRect.setMap(this.googleMap);
            }

            let originalWidth = clusterBounds.getNorthEast().lng() - clusterBounds.getSouthWest().lng();
            let offset = originalWidth*0.1; // offset is 10% of the width;

            // Move the NE corner by the offset
            let neLatLng = new this.googleMapsApi.LatLng({
                lat: clusterBounds.getNorthEast().lat() + offset,
                lng: clusterBounds.getNorthEast().lng() + offset
            });

            // Move the SW corner by the offset
            let swLatLng = new this.googleMapsApi.LatLng({
                lat: clusterBounds.getSouthWest().lat() - offset,
                lng: clusterBounds.getSouthWest().lng() - offset
            });

            clusterBounds.extend(neLatLng);
            clusterBounds.extend(swLatLng);

            // paint the new offset cluster bounds for debugging purposes
            if (debug) {
                let newRect = new this.googleMapsApi.Rectangle({
                    bounds: clusterBounds,
                    strokeColor: '#e3e000',
                    fillOpacity: 0
                });
                newRect.setMap(this.googleMap);
            }

            this.googleMap.fitBounds(clusterBounds);
        });

        this.googleMapsApi.event.addListener(this.googleMap, 'click', function () {
            this.dispatchAction('mapClicked');
        }.bind(this));

        this.googleMapsApi.event.addListener(this.googleMap, 'drag', function () {
            this.dispatchAction('mapDragged');
        }.bind(this));

        this.googleMapsApi.event.addListener(this.googleMap, 'zoom_changed', function () {
            this.setCurrentZoom();
        }.bind(this));

        // When idle we set the marker congestion based on the markers in the map's current bounds.
        // (as opposed to being based on all the map's markers).
        // This is heavy lifting and can result in performance issues - hence this is currently disabled.
        // this.idleTicking = false;
        // this.googleMapsApi.event.addListener(this.googleMap, 'idle', () => {
        //     if (!this.idleTicking) {
        //         requestAnimationFrame((timestamp) => {
        //             this.idleTicking = false;
        //             this.removeHtmlClassState(`congestion_${ this.currentMarkerCongestion }`);
        //             this.currentMarkerCongestion = new MarkerCongestionCalculator(this.getMarkersInBounds().length).congestionLevel;
        //             this.addHtmlClassState(`congestion_${ this.currentMarkerCongestion }`);
        //         });
        //     };
        //     this.idleTicking = true;
        // });

    }

    // getMarkersInBounds() {
    //     const bounds = this.googleMap.getBounds();
    //     return OnlineAppMap.getGoogleMarkers(this.experienceMarkers).filter( (googleMarker) => {
    //         return bounds.contains(googleMarker.getPosition());
    //     });
    // }

    setCurrentMarkerCongestion() {
        this.removeHtmlClassState(`congestion_${ this.currentMarkerCongestion }`);
        this.currentMarkerCongestion = new MarkerCongestionCalculator(this.experienceMarkers.length).congestionLevel;
        this.addHtmlClassState(`congestion_${ this.currentMarkerCongestion }`);
    }

    getGoogleMapOptions() {
        return {
            center: {lat: -41, lng: 172},
            zoom: this.initialZoom,
            minZoom: 5,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeId: 'terrain',
            mapTypeControl: true,
            mapTypeControlOptions: {
                style: this.googleMapsApi.MapTypeControlStyle.DROPDOWN_MENU,
                position: this.googleMapsApi.ControlPosition.TOP_RIGHT
            },
            scrollwheel: false
        };
    }

    getMarkerClusterOptions() {
        const imagePath = `/images/online/apps/${this.onlineApp.code.toLowerCase()}/map/cluster_icon-`;
        const clusterIconPath = `/images/online/apps/${this.onlineApp.code.toLowerCase()}/map/cluster_icon.png`;
        return {
            imagePath: imagePath,
            imageExtension: 'png',
            maxZoom: 12,
            zoomOnClick: false,
            averageCenter: true,
            enableRetinaIcons: true,
            styles: [
                {
                    url: clusterIconPath,
                    textColor: 'white',
                    textSize: 13,
                    height: 53,
                    width: 53
                },
                {
                    url: clusterIconPath,
                    textColor: 'white',
                    textSize: 13,
                    height: 56,
                    width: 56
                },
                {
                    url: clusterIconPath,
                    textColor: 'white',
                    textSize: 13,
                    height: 66,
                    width: 66
                },
                {
                    url: clusterIconPath,
                    textColor: 'white',
                    textSize: 13,
                    height: 78,
                    width: 78
                },
                {
                    url: clusterIconPath,
                    textColor: 'white',
                    textSize: 13,
                    height: 90,
                    width: 90
                },
            ],
            minimumClusterSize: 2
        };
    }

    goToCoords(lat, lng, zoom) {
        const center = new this.googleMapsApi.LatLng(lat, lng);
        this.googleMap.panTo(center);
        if (zoom) {
            this.googleMap.setZoom(parseInt(zoom))
        }
    }

    panBy(xOffset, yOffset) {
        this.googleMap.panBy(xOffset, yOffset);
    }

    replaceAllExperienceMarkers(newExperienceMarkers) {
        this.clearMarkersFromMap();
        this.experienceMarkers = newExperienceMarkers;

        let experienceMarkersWithBenefits = [];
        let experienceMarkersWithOutBenefits = [];

        this.experienceMarkers.forEach( (experienceMarker) => {
            if (experienceMarker.monetizableWithBenefits()) {
                experienceMarkersWithBenefits.push(experienceMarker);
            } else {
                experienceMarkersWithOutBenefits.push(experienceMarker);
            }
        });

        this.setCurrentMarkerCongestion();

        // Configure the clusterer using the calulator and add the experiences markers without benefits to the clusterer
        const calc = new ClustererCongestionCalculator(experienceMarkersWithOutBenefits.length);
        this.markerClusterer.setMaxZoom(calc.maxZoom);
        this.markerClusterer.setMinimumClusterSize(calc.minimumClusterSize);
        this.markerClusterer.addMarkers(OnlineAppMap.getGoogleMarkers(experienceMarkersWithOutBenefits));

        // Add the experience markers with benefits to the google map
        OnlineAppMap.getGoogleMarkers(experienceMarkersWithBenefits).forEach((googleMarker) => {
            googleMarker.setMap(this.googleMap);
        });

        if (this.experienceMarkers.length > 0) {
            this.zoomOutUntilMarkersVisible()
        } else {
            // Zoom to default?
        }
    }

    clearMarkersFromMap() {
        this.markerClusterer.clearMarkers();
        this.experienceMarkers.forEach( (experienceMarker) => {
            experienceMarker.googleMarker.setMap(null);
        });
    }

    setCurrentZoom() {
        this.removeHtmlClassState(`zoom_${ getZoomLabel(this.currentZoom) }`);
        this.currentZoom = this.googleMap.getZoom();
        this.addHtmlClassState(`zoom_${ getZoomLabel(this.currentZoom) }`);
    }

    zoomOutUntilMarkersVisible() {
        if (this.experienceMarkers.length < 1 || !this.googleMap.getBounds()) { return };
        while (!this.experienceMarkers.some(function (experienceMarker, index, array) {
            return this.googleMap.getBounds().contains(experienceMarker.googleMarker.getPosition())
        }.bind(this))) {
            this.googleMap.setZoom(this.googleMap.getZoom() - 1)
        }
    }

    zoomMapToFitExperienceMarkers(experienceMarkers) {
        const bounds = new this.googleMapsApi.LatLngBounds();
        const googleLatLngsPositions = experienceMarkers.map(function(experienceMarker){
            return experienceMarker.googleMarker.getPosition();
        });
        googleLatLngsPositions.forEach( (position) => { bounds.extend(position) } );
        this.googleMap.fitBounds(bounds);
    }

    zoomMapToFitBounds(mapBounds) {
        const bounds = new this.googleMapsApi.LatLngBounds(mapBounds.sw, mapBounds.ne);
        if (!bounds) return;
        this.googleMap.fitBounds(bounds);
    }

    // Render all the ExperienceMarkers for the MapFilter
    renderMapFilter(mapFilter){
        if (!this.onlineApp.isExperienceMarkersLoaded()) { return }

        requestAnimationFrame( () => {
            const filterExperienceMarkers = this.onlineApp.experienceMarkerCollection.allWithExperienceIds(mapFilter.experienceIds);
            this.replaceAllExperienceMarkers(filterExperienceMarkers);
        });
    }

    eraseMapFilter(mapFilter) {
        this.replaceAllExperienceMarkers([]);
    }

    isRenderedWith(mapFilter) {
        return this.currentMapFilter === mapFilter;
    }

    renderGeoData(geoData) {
        if (!!geoData) {
            // Don't add geo data unless the id has changed…
            const newId = geoData['id']; // Our geo data should have an `id` field.
            if (this.currentGeoDataId !== newId) {
                removeGeoData(this.googleMap); // First remove any old data
                addGeoData(this.googleMap, geoData)
            }
            this.currentGeoDataId = newId;
        } else {
            removeGeoData(this.googleMap);
            this.currentGeoDataId = null;
        }
    }

    render() {
        const mapBounds = this.readState('mapBounds');
        if (mapBounds) this.zoomMapToFitBounds(mapBounds);

        this.renderGeoData(this.readState('geoData'));

        const mapFilter = this.readState('mapFilter');
        if ( mapFilter ) {
            this.removeHtmlClassState('no_map_filter');
            if (!this.isRenderedWith(mapFilter)) { this.renderMapFilter(mapFilter) };
        } else {
            this.addHtmlClassState('no_map_filter');
            this.eraseMapFilter(mapFilter);
        }
        this.currentMapFilter = mapFilter;
    }

}

function addGeoData(map, geoData) {
    map.data.addGeoJson(geoData);

    // Set overall styles for the geo data
    const styleOptions = (geoData['styleOptions'] || DefaultGeoDataStyleOptions);
    map.data.setStyle(styleOptions);

    // Override specific feature styles
    map.data.forEach((feature) => {
        const styleOptions = feature.getProperty('styleOptions') || null;
        map.data.overrideStyle(feature, styleOptions);
    });
}

function removeGeoData(map) {
    map.data.forEach((feature) => {
        map.data.remove(feature);
    });
    map.data.setStyle({});
}

// https://developers.google.com/maps/documentation/javascript/reference/data#Data.StyleOptions
const DefaultGeoDataStyleOptions = {
    strokeColor: '#000',
    strokeWeight: 4,
    strokeOpacity: 1
};